"use client";

import { useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../atoms/command";
import { cn } from "../lib";
import { filterBySearch } from "../lib/search-text";

/** The minimum a thing needs to be pickable: something to key it by. */
export type SearchListItem = {
  id: string;
  /** Greys the row out and blocks selection — e.g. already added elsewhere. */
  disabled?: boolean;
};

/**
 * A named section of the list, in the order sections should appear. Rounds you
 * are a member of come before rounds you merely administer; empty sections are
 * skipped rather than shown as a bare heading.
 */
export type SearchListSection = {
  /** Matched against whatever `sectionOf` returns for an item. */
  key: string;
  label: string;
};

/**
 * A searchable list of things to pick from: a search box over a roster the
 * caller supplies and orders, optionally split into named sections.
 *
 * This is the one list every picker in the app is built from — people
 * (`UserSearchList`), rounds, proposals. What differs between them is only
 * *what counts as a match* and *what a row looks like*, so both are props;
 * everything else — the search box, section headings, the empty state, and the
 * rule that a blank query shows everything — is shared.
 *
 * Filtering runs here rather than in the command palette's own matcher, for
 * two reasons. The palette matches the row's value — the item's id — which
 * made an id fragment surface unrelated rows, and it re-sorts by its own
 * relevance score, which would undo the caller's ordering (rounds you're in
 * first, recently used first, and so on).
 */
export function SearchList<T extends SearchListItem>({
  items,
  match,
  renderItem,
  onSelect,
  selectedIds,
  sections,
  sectionOf,
  placeholder = "Search...",
  emptyMessage = "No match found.",
  autoFocus = false,
  disabled = false,
  className,
  footer,
  maxResults,
}: {
  items: readonly T[];
  /** Does this item match what was typed? A blank query must match everything. */
  match: (item: T, query: string) => boolean;
  /**
   * The row's contents. `selected` drives any tick or highlight it shows.
   *
   * Whatever this returns is the command palette's row (`asChild`), so it MUST
   * spread the props it is given onto its root element — that is how it
   * receives the click handler and the listbox roles. A component that takes
   * only its own props silently drops them and renders a row nothing can
   * select. `MultiSelectRow`, `SearchSelectRow` and `UserSearchRow` all
   * forward; copy one of those rather than writing a bare row.
   */
  renderItem: (item: T, state: { selected: boolean }) => React.ReactNode;
  onSelect: (id: string) => void;
  /** Which items read as chosen. Omit for a list with no selection state. */
  selectedIds?: ReadonlySet<string>;
  /** Section headings, in display order. Omit to render one flat list. */
  sections?: readonly SearchListSection[];
  /** Which section an item belongs to. Unrecognised keys fall to the end. */
  sectionOf?: (item: T) => string;
  placeholder?: string;
  emptyMessage?: string;
  autoFocus?: boolean;
  /** Disables every row — for while a selection is being applied. */
  disabled?: boolean;
  className?: string;
  /** Rendered below the list, inside the same surface — counts, a Done button. */
  footer?: React.ReactNode;
  /** Bound expensive row rendering; search still covers the entire list. */
  maxResults?: number;
}) {
  const [search, setSearch] = useState("");
  const allMatches = filterBySearch(items, search, match);
  const matches =
    maxResults == null ? allMatches : allMatches.slice(0, maxResults);

  // Items whose section key isn't one the caller declared would otherwise
  // vanish, which is a silent data-loss bug rather than a layout choice — so
  // they render last, under no heading.
  const grouped: Array<{ key: string; label?: string; items: T[] }> = [];

  if (sections && sectionOf) {
    for (const section of sections) {
      const sectionItems = matches.filter(
        (item) => sectionOf(item) === section.key,
      );

      if (sectionItems.length > 0) {
        grouped.push({ ...section, items: sectionItems });
      }
    }

    const knownKeys = new Set(sections.map((section) => section.key));
    const orphans = matches.filter((item) => !knownKeys.has(sectionOf(item)));

    if (orphans.length > 0) {
      grouped.push({ key: "__other", items: orphans });
    }
  } else if (matches.length > 0) {
    grouped.push({ key: "__all", items: matches });
  }

  return (
    <Command className={cn(className)} shouldFilter={false}>
      <CommandInput
        placeholder={placeholder}
        value={search}
        onValueChange={setSearch}
        autoFocus={autoFocus}
      />
      <CommandList>
        <CommandEmpty>{emptyMessage}</CommandEmpty>
        {grouped.map((section) => (
          <CommandGroup
            key={section.key}
            heading={
              section.label ? (
                <span className="flex items-center justify-between gap-2">
                  <span>{section.label}</span>
                  <span className="tabular-nums">{section.items.length}</span>
                </span>
              ) : undefined
            }
          >
            {section.items.map((item) => (
              <CommandItem
                key={item.id}
                value={item.id}
                // The id comes from the row rather than the palette's callback
                // argument, so nothing downstream depends on how the palette
                // normalizes values.
                onSelect={() => onSelect(item.id)}
                disabled={disabled || item.disabled}
                asChild
              >
                {renderItem(item, {
                  selected: selectedIds?.has(item.id) ?? false,
                })}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
      {matches.length < allMatches.length && (
        <p className="text-muted-foreground body-sm px-3 py-2" role="status">
          Showing {matches.length} of {allMatches.length}. Refine your search.
        </p>
      )}
      {footer}
    </Command>
  );
}
